import {
  getInstance,
  getCleanInstance,
} from '@portal/repositories/securitySdkConfig';
import { FileTypePayable } from '@portal/models/enumerators';

const PAYABLE_BASE_URL = '/payable-group';

const PayableApi = {
  getPayableGroups: async (
    params: advancedFilterModels.baseFilter,
    paymentAccountId: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.get(PAYABLE_BASE_URL, {
      params,
      headers: {
        'payment-account-id': paymentAccountId,
      },
    });

    return data;
  },

  getPayables: async (
    id: string,
    paymentAccountId: string,
    params: advancedFilterModels.PaymentsFileAdvancedFilter
  ) => {
    const instance = getInstance();
    const { data } = await instance.get('/payable', {
      params: {
        ...params,
        groupId: id,
      },
      headers: {
        'payment-account-id': paymentAccountId,
      },
    });

    return data;
  },

  getPayableGroupById: async (id: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${PAYABLE_BASE_URL}/${id}`);

    return data;
  },

  getUrl: async (params: { fileExtension: string }) => {
    const instance = getInstance();
    const { data } = await instance.get(`${PAYABLE_BASE_URL}/upload-url`, {
      params,
    });

    return data;
  },

  getPreview: async (
    params: { bucket: string; name: string; fileType?: FileTypePayable },
    paymentAccountId: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.post(
      `${PAYABLE_BASE_URL}/file-preview`,
      params,
      {
        headers: {
          'payment-account-id': paymentAccountId,
        },
      }
    );

    return data;
  },

  uploadFile: async (
    fileBody: File,
    fileExtension: string,
    urlToUpload: string
  ) => {
    const instance = getCleanInstance(urlToUpload);
    const { data } = await instance.put('', fileBody, {
      headers: {
        'Content-Type': `file/${fileExtension}`,
      },
    });

    return data;
  },

  createFile: async (
    params: {
      bucket: string;
      name: string;
      authenticate: {
        pin: string;
      };
    },
    paymentAccountId: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.post(
      `${PAYABLE_BASE_URL}/file-create`,
      params,
      {
        headers: {
          'payment-account-id': paymentAccountId,
        },
      }
    );

    return data;
  },

  approvePayableGroup: async (
    id: string,
    pin: string,
    paymentAccountId: string
  ) => {
    const instance = getInstance();
    await instance.put(
      `/payable-group/${id}/approve`,
      {
        authenticate: { pin },
      },
      {
        headers: {
          'payment-account-id': paymentAccountId,
        },
      }
    );
  },

  reprovePayableGroup: async (
    id: string,
    pin: string,
    paymentAccountId: string
  ) => {
    const instance = getInstance();
    await instance.put(
      `/payable-group/${id}/reprove`,
      {
        authenticate: { pin },
      },
      {
        headers: {
          'payment-account-id': paymentAccountId,
        },
      }
    );
  },

  bulkApprovePayableGroups: async (
    groupIds: string[],
    pin: string,
    paymentAccountId: string
  ) => {
    const instance = getInstance();
    await instance.patch(
      '/payable-group/respond',
      {
        groupIds,
        approve: true,
        authenticate: { pin },
      },
      {
        headers: {
          'payment-account-id': paymentAccountId,
        },
      }
    );
  },

  bulkReprovePayableGroups: async (
    groupIds: string[],
    pin: string,
    paymentAccountId: string
  ) => {
    const instance = getInstance();
    await instance.patch(
      '/payable-group/respond',
      {
        groupIds,
        approve: false,
        authenticate: { pin },
      },
      {
        headers: {
          'payment-account-id': paymentAccountId,
        },
      }
    );
  },

  getExportedResponseCnab: async (id: string, isEmail: boolean) => {
    const instance = getInstance();
    const { data } = await instance.put(`/payable-group/return-file/${id}`, {
      isEmail,
    });

    return data;
  },

  downloadPaymentExtract: async (
    groupId: string,
    params: advancedFilterModels.baseFilter
  ) => {
    const instance = getInstance();
    const { data } = await instance.get(`/payable/statement/${groupId}`, {
      params,
    });

    return data;
  },

  downloadIndividualStatement: async (id: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`/payable/statement-details/${id}`);

    return data;
  },

  downloadBatchPaymentStatements: async (groupId: string) => {
    const instance = getInstance();
    const { data } = await instance.get(
      `/payable/statement/payable-group/${groupId}`
    );

    return data;
  },
};

export default PayableApi;
